import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clearCache } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AUTH_TOKEN_KEY = "authToken";

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize state from local storage on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const authData = localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY);
        if (authData) {
          const parsed = JSON.parse(authData);
          if (parsed?.isAuthenticated && parsed?.token) {
            setIsAuthenticated(true);
            setUser(parsed.user || null);
            setUserType(parsed.user?.userType || parsed.user?.role?.toLowerCase() || null);
          }
        }
      } catch (error) {
        console.error("Failed to parse auth data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = useCallback((authData, rememberMe) => {
    const role = authData.user?.role || (authData.user?.userType === 'vendor' ? 'vendor' : null);
    const resolvedUserType = authData.user?.userType || (role ? role.toLowerCase() : null);

    const userWithRole = {
      ...(authData.user || {}),
      ...(role ? { role } : {}),
      ...(resolvedUserType ? { userType: resolvedUserType } : {}),
    };

    const dataToStore = {
      token: authData.token,
      user: userWithRole,
      isAuthenticated: true,
      timestamp: new Date().toISOString()
    };
    
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", authData.email || "");
      localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(dataToStore));
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
    } else {
      localStorage.removeItem("rememberedEmail");
      sessionStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(dataToStore));
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }

    setIsAuthenticated(true);
    setUser(userWithRole);
    setUserType(resolvedUserType);
  }, []);

  const updateUser = useCallback((updatedUserData) => {
    setUser((prevUser) => {
      const mergedUser = {
        ...(prevUser || {}),
        ...updatedUserData,
      };

      // Preserve role and userType if not explicitly passed
      if (!mergedUser.role && prevUser?.role) {
        mergedUser.role = prevUser.role;
      }
      if (!mergedUser.userType) {
        mergedUser.userType = prevUser?.userType || (mergedUser.role ? mergedUser.role.toLowerCase() : null);
      }

      // Keep active storage in sync without breaking rememberMe status
      try {
        const storedAuth = localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY);
        if (storedAuth) {
          const parsed = JSON.parse(storedAuth);
          const dataToStore = {
            ...parsed,
            user: mergedUser,
          };
          if (localStorage.getItem(AUTH_TOKEN_KEY)) {
            localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(dataToStore));
          } else {
            sessionStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(dataToStore));
          }
        }
      } catch (err) {
        console.error("Failed to update auth data in storage:", err);
      }

      setUserType(mergedUser.userType || null);
      return mergedUser;
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    clearCache();
    setIsAuthenticated(false);
    setUser(null);
    setUserType(null);
  }, []);

  // Listen for unauthorized 401 events globally
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };
    window.addEventListener('auth_unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth_unauthorized', handleUnauthorized);
  }, [logout]);

  const value = {
    isAuthenticated,
    user,
    userType,
    isLoading,
    login,
    updateUser,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
